import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faTrash,
  faCopy,
  faSync,
  faCode,
  faFilter,
  faSearch,
  faPlus,
  faCalendar,
  faGlobe,
  faTags,
  faFileCode,
} from "@fortawesome/free-solid-svg-icons";
import { HiChevronRight, HiChevronLeft } from "react-icons/hi";
import MessagePopup from "../ComposeMessage/MessagePopup";
import apiEndpoints from "../../apiconfig";

const ManageTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [modal, setModal] = useState({ type: null, data: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [curlModal, setCurlModal] = useState({ open: false, data: null });
  const [payloadModal, setPayloadModal] = useState({ open: false, data: null });
  const [showFilters, setShowFilters] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const templatesPerPage = 8;

  // Fetch Templates
  const fetchTemplates = async (syncWithMeta = false) => {
    setIsLoading(true);
    try {
      const url = syncWithMeta
        ? `${apiEndpoints.managetemplate}?sync=true`
        : apiEndpoints.managetemplate;

      const response = await fetch(url, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });

      const data = await response.json();

      if (data.status === "success" && Array.isArray(data.data)) {
        const formattedTemplates = data.data.map((template) => {
          const bodyComponent = template.components?.find(
            (c) => c.type === "BODY"
          );
          const footerComponent = template.components?.find(
            (c) => c.type === "FOOTER"
          );

          return {
            id: template.id,
            guid: template.id,
            templateName: template.name || "Untitled",
            category: template.category || "N/A",
            meta_status: template.status || "PENDING",
            language: template.language || "en",
            languageGuid: template.language_guid || template.guid || "",
            createdOn: new Date().toISOString(),
            templateBody: bodyComponent?.text || "",
            templateFooter: footerComponent?.text || "",
            type:
              template.category?.toUpperCase() === "MARKETING"
                ? "Promotional"
                : "Transactional",
            attributes:
              bodyComponent?.example?.body_text?.[0]?.map((value, i) => ({
                name: `{{${i + 1}}}`,
                value,
              })) || [],
            rawComponents: template.components || [],
          };
        });

        setTemplates(formattedTemplates);
      } else {
        setError(data.message || "Error fetching templates");
      }
    } catch (error) {
      setError("Failed to fetch templates: " + error.message);
      console.error("Error fetching templates:", error);
    } finally {
      setIsLoading(false);
      setSyncing(false);
    }
  };

  // Auto-sync on mount
  useEffect(() => {
    fetchTemplates(true);
  }, []);

  // Accept template from navigation state
  useEffect(() => {
    if (location.state?.template) {
      const newTemplate = location.state.template;
      const normalized = {
        id: newTemplate.id ?? newTemplate.meta_template_id ?? Date.now(),
        templateName: newTemplate.templateName ?? newTemplate.name ?? "",
        category: newTemplate.category ?? newTemplate.template_category ?? "",
        templateBody: newTemplate.templateBody ?? newTemplate.body ?? "",
        templateFooter: newTemplate.templateFooter ?? "",
        meta_status: newTemplate.meta_status ?? "PENDING",
        createdOn: newTemplate.createdOn ?? new Date().toISOString(),
        attributes: newTemplate.attributes ?? [],
        type: newTemplate.type ?? "Promotional",
        rawComponents: newTemplate.rawComponents ?? newTemplate.components ?? [],
      };
      setTemplates((prev) => [normalized, ...prev]);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Handlers
  const handleSyncWithMeta = () => {
    setSyncing(true);
    fetchTemplates(true);
  };

  const handleDelete = async (template) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${template.templateName}"?`
      )
    ) {
      try {
        const payload = {
          templateId: template.id,
          templateName: template.templateName,
        };

        const response = await fetch(apiEndpoints.managetemplate, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok)
          throw new Error(data.message || "Failed to delete template");

        if (data.status === "success") {
          setTemplates((prev) => prev.filter((t) => t.id !== template.id));
          alert("Template deleted successfully from Meta!");
        } else {
          throw new Error(data.message || "Unknown error occurred");
        }
      } catch (error) {
        console.error("Delete error:", error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleCopy = (template) => {
    navigate("/create-template", { state: { template } });
  };

  const handleCreateTemplate = () => {
    setModal({ type: "create", data: null });
  };

  const handleShowCurl = (template) => {
    const components = [];
    const rawComps = template.rawComponents || [];

    // 1. HEADER
    const headerComp = rawComps.find((c) => c.type === "HEADER");
    if (headerComp) {
      if (["IMAGE", "VIDEO", "DOCUMENT", "AUDIO"].includes(headerComp.format)) {
        components.push({
          type: "header",
          parameters: [
            {
              type: headerComp.format.toLowerCase(),
              [headerComp.format.toLowerCase()]: {
                link: headerComp.example?.header_handle?.[0] || "https://example.com/media.jpg",
              },
            },
          ],
        });
      } else if (headerComp.format === "TEXT" && headerComp.text?.includes("{{1}}")) {
        components.push({
          type: "header",
          parameters: [
            {
              type: "text",
              text: headerComp.example?.header_text?.[0] || "Header Text",
            },
          ],
        });
      }
    }

    // 2. BODY
    const bodyComp = rawComps.find((c) => c.type === "BODY");
    let bodyVariables = [];
    if (bodyComp) {
      const matches = bodyComp.text?.match(/{{\d+}}/g) || [];
      if (matches.length > 0) {
        const bodyParams = [];
        const examples = bodyComp.example?.body_text?.[0] || [];
        matches.forEach((_, idx) => {
          bodyParams.push({
            type: "text",
            text: examples[idx] || "123456",
          });
          bodyVariables.push(examples[idx] || "123456");
        });
        components.push({
          type: "body",
          parameters: bodyParams,
        });
      }
    }

    // 3. BUTTONS
    const buttonsComp = rawComps.find((c) => c.type === "BUTTONS");
    const localBtns = [];
    if (buttonsComp && Array.isArray(buttonsComp.buttons)) {
      buttonsComp.buttons.forEach((btn, index) => {
        if (btn.type === "URL" && btn.url?.includes("{{1}}")) {
          const examples = btn.example || [];
          components.push({
            type: "button",
            sub_type: "url",
            index: String(index),
            parameters: [
              {
                type: "text",
                text: examples[0] || (bodyComp?.example?.body_text?.[0]?.[0] || "123456"),
              },
            ],
          });
          localBtns.push({
            type: "URL",
            url: btn.url,
          });
        } else if (btn.type === "QUICK_REPLY") {
          components.push({
            type: "button",
            sub_type: "quick_reply",
            index: String(index),
            parameters: [
              {
                type: "payload",
                payload: "payload_data",
              },
            ],
          });
          localBtns.push({
            type: "QUICK_REPLY",
            payload: "payload_data",
          });
        }
      });
    }

    // fallback for legacy template objects if rawComponents is empty
    if (components.length === 0 && template.attributes && Array.isArray(template.attributes) && template.attributes.length > 0) {
      const bodyParams = template.attributes.map((attr, index) => ({
        type: "text",
        text: attr.value || `{{${index + 1}}}`,
      }));
      components.push({
        type: "body",
        parameters: bodyParams,
      });
      bodyVariables = template.attributes.map((attr, index) => attr.value || `{{${index + 1}}}`);
    }

    // Option 1: Direct Meta WhatsApp Cloud API
    const curlJSON = {
      messaging_product: "whatsapp",
      to: "<PHONE_NUMBER>",
      type: "template",
      template: {
        name: template.templateName,
        language: { code: template.language || "en_US" },
      },
    };

    if (components.length > 0) {
      curlJSON.template.components = components;
    }

    const metaCurlCommand = `curl -X POST "https://graph.facebook.com/v20.0/<PHONE_NUMBER_ID>/messages" \\
-H "Authorization: Bearer <ACCESS_TOKEN>" \\
-H "Content-Type: application/json" \\
-d '${JSON.stringify(curlJSON, null, 2)}'`;

    // Option 2: Unified Megabridge Local API
    const localPayload = {
      to: "<PHONE_NUMBER>",
      template_name: template.templateName,
      language: template.language || "en",
    };

    if (bodyVariables.length > 0) {
      localPayload.variables = bodyVariables;
    }

    if (headerComp) {
      if (["IMAGE", "VIDEO", "DOCUMENT", "AUDIO"].includes(headerComp.format)) {
        localPayload.header = {
          type: headerComp.format.toLowerCase(),
          link: headerComp.example?.header_handle?.[0] || "https://example.com/media.jpg",
        };
        if (headerComp.format === "DOCUMENT") {
          localPayload.header.filename = "Document.pdf";
        }
      } else if (headerComp.format === "TEXT" && headerComp.text?.includes("{{1}}")) {
        localPayload.header = {
          type: "text",
          text: headerComp.example?.header_text?.[0] || "Header Text",
        };
      }
    }

    if (localBtns.length > 0) {
      localPayload.buttons = localBtns;
    }

    const localCurlCommand = `curl -X POST "http://localhost/megabridge/v1/sms/message.php" \\
-H "Authorization: Bearer <JWT_PERMANENT_TOKEN>" \\
-H "Content-Type: application/json" \\
-d '${JSON.stringify(localPayload, null, 2)}'`;

    const combinedOutput = `# Option 1: Direct Meta WhatsApp Cloud API
${metaCurlCommand}

# =========================================================================

# Option 2: Unified Megabridge Local API (Sends to message.php)
${localCurlCommand}`;

    setCurlModal({ open: true, data: combinedOutput });
  };

  const handleShowPayload = (template) => {
    const rawComps = template.rawComponents || [];
    const bodyComp = rawComps.find((c) => c.type === "BODY");
    const headerComp = rawComps.find((c) => c.type === "HEADER");
    const buttonsComp = rawComps.find((c) => c.type === "BUTTONS");

    const bodyVariables = [];
    if (bodyComp) {
      const matches = bodyComp.text?.match(/{{\d+}}/g) || [];
      if (matches.length > 0) {
        const examples = bodyComp.example?.body_text?.[0] || [];
        matches.forEach((_, idx) => {
          bodyVariables.push(examples[idx] || "123456");
        });
      }
    }

    const localBtns = [];
    if (buttonsComp && Array.isArray(buttonsComp.buttons)) {
      buttonsComp.buttons.forEach((btn) => {
        if (btn.type === "URL" && btn.url?.includes("{{1}}")) {
          localBtns.push({
            type: "URL",
            url: btn.url,
          });
        } else if (btn.type === "QUICK_REPLY") {
          localBtns.push({
            type: "QUICK_REPLY",
            payload: "payload_data",
          });
        }
      });
    }

    if (bodyVariables.length === 0 && template.attributes && Array.isArray(template.attributes) && template.attributes.length > 0) {
      template.attributes.forEach((attr) => {
        bodyVariables.push(attr.value || "123456");
      });
    }

    const localPayload = {
      to: "<PHONE_NUMBER>",
      template_name: template.templateName,
      language: template.language || "en",
    };

    if (bodyVariables.length > 0) {
      localPayload.variables = bodyVariables;
    }

    if (headerComp) {
      if (["IMAGE", "VIDEO", "DOCUMENT", "AUDIO"].includes(headerComp.format)) {
        localPayload.header = {
          type: headerComp.format.toLowerCase(),
          link: headerComp.example?.header_handle?.[0] || "https://example.com/media.jpg",
        };
        if (headerComp.format === "DOCUMENT") {
          localPayload.header.filename = "Document.pdf";
        }
      } else if (headerComp.format === "TEXT" && headerComp.text?.includes("{{1}}")) {
        localPayload.header = {
          type: "text",
          text: headerComp.example?.header_text?.[0] || "Header Text",
        };
      }
    }

    if (localBtns.length > 0) {
      localPayload.buttons = localBtns;
    }

    setPayloadModal({ open: true, data: JSON.stringify(localPayload, null, 2) });
  };

  const closeModal = () => setModal({ type: null, data: null });

  // Filters & Pagination
  const filteredTemplates = templates.filter((t) => {
    const matchesSearch = t.templateName
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory
      ? t.category === selectedCategory
      : true;
    const matchesType = selectedType ? t.type === selectedType : true;
    return matchesSearch && matchesCategory && matchesType;
  });

  const indexOfLastTemplate = currentPage * templatesPerPage;
  const indexOfFirstTemplate = indexOfLastTemplate - templatesPerPage;
  const currentTemplates = filteredTemplates.slice(
    indexOfFirstTemplate,
    indexOfLastTemplate
  );
  const totalPages = Math.ceil(filteredTemplates.length / templatesPerPage);

  const handleNextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handlePrevPage = () =>
    currentPage > 1 && setCurrentPage(currentPage - 1);

  const categories = [...new Set(templates.map((t) => t.category))];
  const types = [...new Set(templates.map((t) => t.type))];

  // Status chip styling
  const statusChip = (status) => {
    const s = (status || "PENDING").toUpperCase();
    if (s === "APPROVED") return "bg-emerald-500 text-white";
    if (s === "REJECTED") return "bg-red-500 text-white";
    if (s === "IN_REVIEW") return "bg-amber-500 text-white";
    if (s === "PENDING") return "bg-gray-500 text-white";
    return "bg-blue-500 text-white";
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-800">
            Loading Templates
          </h3>
          <p className="text-gray-600">
            Please wait while we fetch your templates...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Error Loading Templates
          </h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-50 p-3 sm:p-4 md:p-6"
      style={{ fontFamily: "Montserrat, sans-serif" }}
    >
      {/* Header Section */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Template Management
            </h1>
            <div className="flex items-center text-sm text-gray-600">
              <span className="text-gray-500">Dashboard</span>
              <HiChevronRight className="mx-2 text-gray-400" />
              <span className="text-emerald-600 font-medium">
                Manage Templates
              </span>
            </div>
          </div>

          <div className="flex flex-row xs:flex-row gap-2">
            <button
              onClick={handleSyncWithMeta}
              disabled={syncing}
              className="inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base"
            >
              <FontAwesomeIcon
                icon={faSync}
                className={syncing ? "animate-spin" : ""}
              />
              {syncing ? "Syncing..." : "Sync Status"}
            </button>
            <button
              onClick={handleCreateTemplate}
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 sm:px-4 py-2.5 rounded-lg transition-colors font-medium text-sm sm:text-base shadow-sm"
            >
              <FontAwesomeIcon icon={faPlus} />
              Create Template
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search Input */}
            <div className="flex-1 relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                placeholder="Search templates by name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm sm:text-base"
              />
            </div>

            {/* Filter Toggle for Mobile */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden inline-flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg transition-colors font-medium text-sm"
            >
              <FontAwesomeIcon icon={faFilter} />
              Filters
            </button>

            {/* Filters - Desktop */}
            <div className="hidden lg:flex gap-3">
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="min-w-[140px] border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-gray-700 text-sm"
              >
                <option value="">All Categories</option>
                {categories.map((c, idx) => (
                  <option key={idx} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setCurrentPage(1);
                }}
                className="min-w-[140px] border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-gray-700 text-sm"
              >
                <option value="">All Types</option>
                {types.map((t, idx) => (
                  <option key={idx} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-200">
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-gray-700 text-sm"
              >
                <option value="">All Categories</option>
                {categories.map((c, idx) => (
                  <option key={idx} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-gray-700 text-sm"
              >
                <option value="">All Types</option>
                {types.map((t, idx) => (
                  <option key={idx} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Results Count and Clear Filters */}
        <div className="flex flex-col xs:flex-row justify-between items-start xs:items-center gap-2  mb-4">
          <div className="text-gray-700 text-sm sm:text-base">
            <span className="font-semibold">{filteredTemplates.length}</span>{" "}
            templates found
          </div>
          {(selectedCategory || selectedType || searchQuery) && (
            <button
              onClick={() => {
                setSelectedCategory("");
                setSelectedType("");
                setSearchQuery("");
                setCurrentPage(1);
              }}
              className="text-emerald-600 hover:text-emerald-700 font-medium text-sm"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Templates Grid */}
        {currentTemplates.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 sm:gap-6 mb-8">
            {currentTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-xl shadow-xs border border-gray-200 hover:shadow-md transition-all duration-300 overflow-hidden group"
              >
                {/* Card Header */}
                <div className="p-4 sm:p-5 border-b border-gray-100">
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate flex-1 pr-2">
                      {template.templateName}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${statusChip(
                        template.meta_status
                      )} whitespace-nowrap`}
                    >
                      {template.meta_status || "PENDING"}
                    </span>
                  </div>
                  <div className="flex items-center text-xs text-gray-500 gap-2">
                    <div className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faCalendar} className="text-xs" />
                      <span>
                        {new Date(template.createdOn).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faGlobe} className="text-xs" />
                      <span>{template.language?.toUpperCase()}</span>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4 sm:p-5">
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 min-h-[80px] max-h-[80px] overflow-hidden">
                    <p className="text-gray-700 text-sm line-clamp-3 whitespace-pre-wrap leading-relaxed">
                      {template.templateBody || "No content available"}
                    </p>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-200 flex items-center gap-1">
                      <FontAwesomeIcon icon={faTags} className="text-xs" />
                      {template.type}
                    </span>
                    <span className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-full text-xs font-medium border border-gray-200">
                      {template.category}
                    </span>
                    {Array.isArray(template.attributes) &&
                      template.attributes.length > 0 && (
                        <span className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                          {template.attributes.length} variable
                          {template.attributes.length > 1 ? "s" : ""}
                        </span>
                      )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center gap-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          navigate("/create-template", {
                            state: {
                              view: true,
                              id: template.id,
                              name: template.templateName,
                            },
                          })
                        }
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs sm:text-sm text-gray-700 hover:text-emerald-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-lg flex-1 xs:flex-none justify-center"
                      >
                        <FontAwesomeIcon icon={faEye} />
                        View
                      </button>
                      <button
                        onClick={() => handleCopy(template)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs sm:text-sm text-gray-700 hover:text-emerald-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-lg flex-1 xs:flex-none justify-center"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                        Duplicate
                      </button>
                    </div>
                    <div className="flex gap-1 justify-center xs:justify-start">
                      <button
                        onClick={() => handleShowCurl(template)}
                        className="p-2 text-gray-500 hover:text-emerald-600 transition-colors rounded-lg hover:bg-gray-100"
                        title="Show cURL"
                      >
                        <FontAwesomeIcon icon={faCode} className="text-sm" />
                      </button>
                      <button
                        onClick={() => handleShowPayload(template)}
                        className="p-2 text-gray-500 hover:text-emerald-600 transition-colors rounded-lg hover:bg-gray-100"
                        title="Show Payload"
                      >
                        <FontAwesomeIcon icon={faFileCode} className="text-sm" />
                      </button>
                      <button
                        onClick={() => handleDelete(template)}
                        className="p-2 text-gray-500 hover:text-red-600 transition-colors rounded-lg hover:bg-gray-100"
                        title="Delete Template"
                      >
                        <FontAwesomeIcon icon={faTrash} className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-8 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No templates found
              </h3>
              <p className="text-gray-600 mb-6 text-sm">
                {searchQuery || selectedCategory || selectedType
                  ? "Try adjusting your filters to see more results."
                  : "Get started by creating your first template."}
              </p>
              <button
                onClick={handleCreateTemplate}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors inline-flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faPlus} />
                Create Template
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {filteredTemplates.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl shadow-xs border border-gray-200 p-4 sm:p-6">
            <div className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-medium">{indexOfFirstTemplate + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(indexOfLastTemplate, filteredTemplates.length)}
              </span>{" "}
              of <span className="font-medium">{filteredTemplates.length}</span>{" "}
              results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <HiChevronLeft className="text-lg" />
              </button>
              <span className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg font-medium border border-emerald-200 text-sm">
                {currentPage}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <HiChevronRight className="text-lg" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      {modal.type === "create" && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 mx-2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Create New Template
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div
                onClick={() => {
                  closeModal();
                  navigate("/create-template");
                }}
                className="border-2 border-dashed border-emerald-300 rounded-xl p-5 hover:bg-emerald-50 cursor-pointer transition-all duration-200 hover:border-emerald-400 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <svg
                      className="w-5 h-5 text-emerald-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                      Start from Scratch
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      Create a custom template from blank
                    </p>
                  </div>
                </div>
              </div>

              {/* <div
                onClick={() => {
                  closeModal();
                  navigate("/use-templates");
                }}
                className="border-2 border-dashed border-blue-300 rounded-xl p-5 hover:bg-blue-50 cursor-pointer transition-all duration-200 hover:border-blue-400 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Use Predefined Template</h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Choose from Meta's template library</p>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      )}

      {/* cURL Modal */}
      {curlModal.open && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col mx-2">
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                cURL Command
              </h2>
              <button
                onClick={() => setCurlModal({ open: false, data: null })}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 flex-1 overflow-auto">
              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs sm:text-sm overflow-x-auto">
                {curlModal.data}
              </pre>
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(curlModal.data);
                  alert("Copied to clipboard!");
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg font-medium transition-colors text-sm sm:text-base"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payload Modal */}
      {payloadModal.open && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col mx-2">
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Local API Payload Template
              </h2>
              <button
                onClick={() => setPayloadModal({ open: false, data: null })}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 flex-1 overflow-auto">
              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs sm:text-sm overflow-x-auto font-mono">
                {payloadModal.data}
              </pre>
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(payloadModal.data);
                  alert("Payload copied to clipboard!");
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg font-medium transition-colors text-sm sm:text-base"
              >
                Copy Payload JSON
              </button>
            </div>
          </div>
        </div>
      )}
      {showMessagePopup && (
        <MessagePopup
          templates={templates}
          onClose={() => setShowMessagePopup(false)}
        />
      )}
    </div>
  );
};

export default ManageTemplates;
